import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { AdminFee } from '../../../../services/admin-fees/admin-fees.service';
import { Partner } from '../../../../services/partners/partners.service';
import { VendorProduct } from '../../../../services/vendorproducts/vendorproducts.service';

type RuleCondition = 'ALL' | 'ONLY' | 'EXCEPT';
type FeeChargeMode = 'bundled' | 'line_item';
const SELECT_VENDOR_VALUE = '__SELECT_VENDOR__';
const MORE_INFO_HTML_MAX_LENGTH = 10000;
const SHOPPING_CART_CONFIG_KEYS = ['shoppingCart', 'hideEnrollButtonForShoppingCart'] as const;

type NormalizedRule = {
  amountCents: number | null;
  planCondition: RuleCondition;
  planLevels: string[];
  stateCondition: RuleCondition;
  states: string[];
};

export type AdminFeeEditorDialogData = {
  mode: 'create' | 'edit';
  partners: Partner[];
  vendorProducts: VendorProduct[];
  adminFee?: AdminFee | null;
};

export type BrandShoppingCartConfigUpdate = {
  partnerId: string;
  configjson: Record<string, any>;
};

export type AdminFeeEditorDialogResult = {
  vendorId: string;
  amountCents: number;
  appliesWhen: Record<string, any>;
  brandConfigUpdate?: BrandShoppingCartConfigUpdate;
};

@Component({
    selector: 'app-admin-fee-editor-dialog',
    templateUrl: './admin-fee-editor-dialog.component.html',
    styleUrls: ['./admin-fee-editor-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AdminFeeEditorDialogComponent implements OnInit {
  protected form: UntypedFormGroup;
  protected readonly moreInfoHtmlMaxLength = MORE_INFO_HTML_MAX_LENGTH;
  protected submitAttempted = false;
  protected duplicateRuleError: string | null = null;
  protected previewAmountCents: number | null = null;
  protected previewMatchedRuleSummary = 'Default amount';
  protected previewWarning: string | null = null;
  protected vendorSearchControl = new UntypedFormControl('');
  protected vendorProductSearchControl = new UntypedFormControl('');
  protected vendorOptions: Array<{ label: string; value: string }> = [];
  protected vendorProductOptions: Array<{ label: string; value: string }> = [];
  protected filteredVendorOptions: Array<{ label: string; value: string }> = [];
  protected filteredVendorProductOptions: Array<{ label: string; value: string }> = [];
  protected planLevelSuggestions: string[] = [];

  protected readonly stateOptions = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
  ];

  constructor(
    private fb: UntypedFormBuilder,
    private dialogRef: MatDialogRef<AdminFeeEditorDialogComponent, AdminFeeEditorDialogResult | null>,
    @Inject(MAT_DIALOG_DATA) protected data: AdminFeeEditorDialogData
  ) {
    this.form = this.fb.group({
      vendorId: [SELECT_VENDOR_VALUE, Validators.required],
      shoppingCart: [false],
      hideEnrollButtonForShoppingCart: [false],
      amountCents: [0, [Validators.required, Validators.min(0)]],
      appliesToAllProducts: [false],
      chargeMode: ['bundled', Validators.required],
      stripeProductId: [''],
      vendorProductId: [''],
      cartLabel: [''],
      moreInfoHtml: ['', [Validators.maxLength(MORE_INFO_HTML_MAX_LENGTH)]],
      useConditionalRules: [false],
      defaultAmountCents: [0, [Validators.min(0)]],
      previewPlanLevel: [''],
      previewState: [''],
      rules: this.fb.array([]),
    });
  }

  ngOnInit(): void {
    this.vendorOptions = this.buildVendorOptions();
    this.vendorProductOptions = this.buildVendorProductOptions();
    this.filteredVendorOptions = [...this.vendorOptions];
    this.filteredVendorProductOptions = [...this.vendorProductOptions];
    this.vendorSearchControl.valueChanges.subscribe((searchValue) => {
      this.applyVendorFilter(searchValue);
    });
    this.vendorProductSearchControl.valueChanges.subscribe((searchValue) => {
      this.applyVendorProductFilter(searchValue);
    });
    this.initializeFormFromData();
    this.applySelectedBrandConfigToForm();
    this.form.get('vendorId')?.valueChanges.subscribe(() => {
      this.applySelectedBrandConfigToForm();
      this.refreshDerivedState();
    });
    this.form.valueChanges.subscribe(() => {
      this.refreshDerivedState();
    });
    this.refreshDerivedState();
  }

  protected get isEditMode(): boolean {
    return this.data.mode === 'edit';
  }

  protected get dialogTitle(): string {
    return this.isEditMode ? 'Edit Admin Fee' : 'Add Admin Fee';
  }

  protected get canEditSelectedBrandConfig(): boolean {
    return !!this.getSelectedPartner();
  }

  protected get selectedBrandConfigMessage(): string {
    const vendorId = this.getSelectedVendorId();
    if (!vendorId || vendorId === SELECT_VENDOR_VALUE) {
      return 'Select a brand to view and update its shopping cart settings.';
    }
    if (vendorId === 'ALL') {
      return 'Shopping cart settings are brand-specific and cannot be updated for All brands.';
    }

    const shoppingCartEnabled = Boolean(this.form.get('shoppingCart')?.value);
    const shoppingCartOnly = Boolean(this.form.get('hideEnrollButtonForShoppingCart')?.value);
    if (!shoppingCartEnabled) {
      return 'This brand is not configured for the shopping cart. Admin fees may not appear in the customer cart experience.';
    }
    if (!shoppingCartOnly) {
      return 'Shopping cart is enabled, but the standard enroll button is still available for this brand.';
    }
    return 'This brand is configured for the shopping-cart-only experience.';
  }

  private buildVendorOptions(): Array<{ label: string; value: string }> {
    const partnerOptions = (this.data.partners || []).map((partner) => ({
      label: String(partner.name || partner.partnerId || ''),
      value: String(partner.partnerId || ''),
    }));
    return [
      { label: 'Select brand', value: SELECT_VENDOR_VALUE },
      { label: 'All brands', value: 'ALL' },
      ...partnerOptions
    ];
  }

  private buildVendorProductOptions(): Array<{ label: string; value: string }> {
    return (this.data.vendorProducts || []).map((vendorProduct) => ({
      label: String(vendorProduct.productName || vendorProduct.id || ''),
      value: String(vendorProduct.id || ''),
    }));
  }

  private applyVendorFilter(rawSearchValue: unknown): void {
    const searchValue = this.normalizeSearchValue(rawSearchValue);
    if (!searchValue) {
      this.filteredVendorOptions = [...this.vendorOptions];
      return;
    }
    this.filteredVendorOptions = this.vendorOptions.filter((option) => {
      return option.label.toLowerCase().includes(searchValue)
        || option.value.toLowerCase().includes(searchValue);
    });
  }

  private applyVendorProductFilter(rawSearchValue: unknown): void {
    const searchValue = this.normalizeSearchValue(rawSearchValue);
    if (!searchValue) {
      this.filteredVendorProductOptions = [...this.vendorProductOptions];
      return;
    }
    this.filteredVendorProductOptions = this.vendorProductOptions.filter((option) => {
      return option.label.toLowerCase().includes(searchValue)
        || option.value.toLowerCase().includes(searchValue);
    });
  }

  private normalizeSearchValue(rawSearchValue: unknown): string {
    if (typeof rawSearchValue !== 'string') {
      return '';
    }
    return rawSearchValue.trim().toLowerCase();
  }

  protected get rules(): UntypedFormArray {
    return this.form.get('rules') as UntypedFormArray;
  }

  private buildPlanLevelSuggestions(): string[] {
    const planSet = new Set<string>();
    this.rules.controls.forEach((ruleControl) => {
      const planLevelsInput = (ruleControl.get('planLevelsInput')?.value || '') as string;
      this.parseCsv(planLevelsInput).forEach((plan) => {
        planSet.add(plan);
      });
    });
    return Array.from(planSet).sort((a, b) => a.localeCompare(b));
  }

  protected addRule(): void {
    this.rules.push(this.createRuleGroup());
    this.refreshDerivedState();
  }

  protected removeRule(index: number): void {
    if (index < 0 || index >= this.rules.length) {
      return;
    }
    this.rules.removeAt(index);
    this.refreshDerivedState();
  }

  protected onConditionalToggle(checked: boolean): void {
    if (checked) {
      if (!this.rules.length) {
        this.addRule();
      }
      const amountCents = this.toWholeNumber(this.form.get('amountCents')?.value);
      this.form.patchValue(
        { defaultAmountCents: amountCents === null || amountCents < 0 ? 0 : amountCents },
        { emitEvent: false }
      );
    } else {
      while (this.rules.length > 0) {
        this.rules.removeAt(0);
      }
    }
    this.refreshDerivedState();
  }

  protected planLevelsRequired(ruleControl: any): boolean {
    return this.getRuleCondition(ruleControl as UntypedFormGroup, 'planCondition') !== 'ALL';
  }

  protected statesRequired(ruleControl: any): boolean {
    return this.getRuleCondition(ruleControl as UntypedFormGroup, 'stateCondition') !== 'ALL';
  }

  protected isRuleInvalid(ruleControl: any): boolean {
    const group = ruleControl as UntypedFormGroup;
    const amountCents = this.toWholeNumber(group.get('amountCents')?.value);
    if (amountCents === null || amountCents < 0) {
      return true;
    }
    if (this.planLevelsRequired(group) && this.parseCsv(group.get('planLevelsInput')?.value).length === 0) {
      return true;
    }
    if (this.statesRequired(group) && this.normalizeStates(group.get('states')?.value).length === 0) {
      return true;
    }
    return false;
  }

  protected asFormGroup(control: any): UntypedFormGroup {
    return control as UntypedFormGroup;
  }

  protected canSave(): boolean {
    const vendorId = this.getSelectedVendorId();
    if (!vendorId || vendorId === SELECT_VENDOR_VALUE) {
      return false;
    }

    const amountCents = this.toWholeNumber(this.form.get('amountCents')?.value);
    if (amountCents === null || amountCents < 0) {
      return false;
    }
    if (this.form.get('moreInfoHtml')?.invalid) {
      return false;
    }

    const appliesToAllProducts = Boolean(this.form.get('appliesToAllProducts')?.value);
    const requiresStripeProduct = this.requiresStripeProduct();
    const stripeProductId = String(this.form.get('stripeProductId')?.value || '').trim();
    const vendorProductId = String(this.form.get('vendorProductId')?.value || '').trim();
    if (requiresStripeProduct && !stripeProductId) {
      return false;
    }
    if (!appliesToAllProducts && !vendorProductId) {
      return false;
    }

    const useConditionalRules = Boolean(this.form.get('useConditionalRules')?.value);
    if (useConditionalRules) {
      const defaultAmountCents = this.toWholeNumber(this.form.get('defaultAmountCents')?.value);
      if (defaultAmountCents === null || defaultAmountCents < 0) {
        return false;
      }
      if (!this.rules.length) {
        return false;
      }
      if (this.rules.controls.some((ruleControl) => this.isRuleInvalid(ruleControl as UntypedFormGroup))) {
        return false;
      }
      if (this.duplicateRuleError) {
        return false;
      }
    }

    return true;
  }

  protected showVendorRequiredError(): boolean {
    const vendorControl = this.form.get('vendorId');
    if (!vendorControl) {
      return false;
    }
    const vendorId = String(vendorControl.value || '').trim();
    const hasSelectedVendor = Boolean(vendorId) && vendorId !== SELECT_VENDOR_VALUE;
    return !hasSelectedVendor && (this.submitAttempted || vendorControl.touched || vendorControl.dirty);
  }

  protected cancel(): void {
    this.dialogRef.close(null);
  }

  protected save(): void {
    if (!this.canSave()) {
      this.submitAttempted = true;
      this.form.markAllAsTouched();
      return;
    }

    const appliesWhen: Record<string, any> = {};
    const appliesToAllProducts = Boolean(this.form.get('appliesToAllProducts')?.value);
    const chargeMode = this.getChargeMode();
    appliesWhen.always = appliesToAllProducts;
    appliesWhen.chargeMode = chargeMode;

    const stripeProductId = String(this.form.get('stripeProductId')?.value || '').trim();
    const vendorProductId = String(this.form.get('vendorProductId')?.value || '').trim();
    if (this.requiresStripeProduct() && stripeProductId) {
      appliesWhen.stripeProductId = stripeProductId;
    }
    if (!appliesToAllProducts && vendorProductId) {
      appliesWhen.vendorProductId = vendorProductId;
    }

    const cartLabel = String(this.form.get('cartLabel')?.value || '').trim();
    if (cartLabel) {
      appliesWhen.cartLabel = cartLabel;
    }
    const moreInfoHtml = this.normalizeOptionalHtml(this.form.get('moreInfoHtml')?.value);
    if (moreInfoHtml) {
      appliesWhen.moreInfoHtml = moreInfoHtml;
    }

    let amountCents = this.toWholeNumber(this.form.get('amountCents')?.value);
    if (amountCents === null || amountCents < 0) {
      amountCents = 0;
    }

    if (Boolean(this.form.get('useConditionalRules')?.value)) {
      const defaultAmountCents = this.toWholeNumber(this.form.get('defaultAmountCents')?.value);
      const fallbackDefaultAmount = defaultAmountCents === null ? amountCents : defaultAmountCents;
      const serializedRules = this.compileRulesForStorage()
        .map((rule) => {
          const serializedRule: Record<string, any> = {
            amountCents: rule.amountCents,
          };
          if (rule.includePlans.length > 0) {
            serializedRule.planLevels = rule.includePlans;
          }
          if (rule.excludePlans.length > 0) {
            serializedRule.excludePlanLevels = rule.excludePlans;
          }
          if (rule.includeStates.length > 0) {
            serializedRule.states = rule.includeStates;
          }
          if (rule.excludeStates.length > 0) {
            serializedRule.excludeStates = rule.excludeStates;
          }
          return serializedRule;
        });

      appliesWhen.defaultAmountCents = fallbackDefaultAmount;
      appliesWhen.amountRules = {
        defaultAmountCents: fallbackDefaultAmount,
        rules: serializedRules,
      };
      amountCents = fallbackDefaultAmount;
    }

    const selectedVendorId = String(this.form.get('vendorId')?.value || '').trim();
    const dialogResult: AdminFeeEditorDialogResult = {
      vendorId: selectedVendorId === SELECT_VENDOR_VALUE ? '' : selectedVendorId,
      amountCents,
      appliesWhen,
    };
    const brandConfigUpdate = this.buildBrandShoppingCartConfigUpdate();
    if (brandConfigUpdate) {
      dialogResult.brandConfigUpdate = brandConfigUpdate;
    }
    this.dialogRef.close(dialogResult);
  }

  private initializeFormFromData(): void {
    const adminFee = this.data.adminFee || null;
    const appliesWhen = (adminFee?.appliesWhen || {}) as Record<string, any>;
    const amountCents = this.toWholeNumber(adminFee?.amountCents);
    const normalizedAmountCents = amountCents === null || amountCents < 0 ? 0 : amountCents;
    const normalizedRuleData = this.normalizeRulesFromAppliesWhen(appliesWhen, normalizedAmountCents);
    const useConditionalRules = normalizedRuleData.rules.length > 0;

    const defaultVendorId = this.data.mode === 'create' ? SELECT_VENDOR_VALUE : 'ALL';
    const selectedVendorId = this.data.mode === 'create'
      ? SELECT_VENDOR_VALUE
      : (adminFee?.vendorId ?? defaultVendorId);
    this.form.patchValue({
      vendorId: selectedVendorId,
      amountCents: normalizedAmountCents,
      appliesToAllProducts: Boolean(appliesWhen.always),
      chargeMode: this.normalizeChargeMode(
        appliesWhen.chargeMode,
        Boolean(appliesWhen.always),
        Boolean(String(appliesWhen.stripeProductId || '').trim())
      ),
      stripeProductId: String(appliesWhen.stripeProductId || ''),
      vendorProductId: String(appliesWhen.vendorProductId || ''),
      cartLabel: String(appliesWhen.cartLabel || appliesWhen.feeName || ''),
      moreInfoHtml: String(appliesWhen.moreInfoHtml || appliesWhen.moreInfo || ''),
      useConditionalRules,
      defaultAmountCents: normalizedRuleData.defaultAmountCents,
      previewState: '',
      previewPlanLevel: '',
    }, { emitEvent: false });

    normalizedRuleData.rules.forEach((rule) => {
      this.rules.push(this.createRuleGroup(rule));
    });
    if (useConditionalRules && !this.rules.length) {
      this.rules.push(this.createRuleGroup());
    }
  }

  private createRuleGroup(initialRule?: Partial<NormalizedRule>): UntypedFormGroup {
    return this.fb.group({
      amountCents: [initialRule?.amountCents ?? null, [Validators.required, Validators.min(0)]],
      planCondition: [initialRule?.planCondition || 'ALL'],
      planLevelsInput: [(initialRule?.planLevels || []).join(', ')],
      stateCondition: [initialRule?.stateCondition || 'ALL'],
      states: [initialRule?.states || []],
    });
  }

  private refreshDerivedState(): void {
    this.enforceScopeAndChargeModeConstraints();
    this.duplicateRuleError = this.computeDuplicateRuleError();
    this.planLevelSuggestions = this.buildPlanLevelSuggestions();
    this.refreshPreview();
  }

  protected getBrandConfigValueLabel(configKey: typeof SHOPPING_CART_CONFIG_KEYS[number]): string {
    const partner = this.getSelectedPartner();
    if (!partner) {
      return 'Not available';
    }

    const currentState = this.getPartnerShoppingCartConfigState(partner);
    const currentValue = currentState[configKey];
    const currentLabel = currentValue.exists ? String(currentValue.value) : 'missing';
    const control = this.form.get(configKey);
    if (!control?.dirty) {
      return currentLabel;
    }

    const nextLabel = String(Boolean(control.value));
    return currentLabel === nextLabel ? currentLabel : `${currentLabel} -> ${nextLabel}`;
  }

  private applySelectedBrandConfigToForm(): void {
    const partner = this.getSelectedPartner();
    const state = partner ? this.getPartnerShoppingCartConfigState(partner) : null;

    this.form.patchValue({
      shoppingCart: Boolean(state?.shoppingCart.value),
      hideEnrollButtonForShoppingCart: Boolean(state?.hideEnrollButtonForShoppingCart.value),
    }, { emitEvent: false });

    for (const configKey of SHOPPING_CART_CONFIG_KEYS) {
      const control = this.form.get(configKey);
      if (!control) {
        continue;
      }
      if (partner) {
        control.enable({ emitEvent: false });
      } else {
        control.disable({ emitEvent: false });
      }
      control.markAsPristine();
      control.markAsUntouched();
    }
  }

  private buildBrandShoppingCartConfigUpdate(): BrandShoppingCartConfigUpdate | null {
    const partner = this.getSelectedPartner();
    if (!partner?.partnerId) {
      return null;
    }

    const currentState = this.getPartnerShoppingCartConfigState(partner);
    const configjson = this.clonePartnerConfigJson(partner.configjson);
    let hasChanges = false;

    configjson.content = configjson.content && typeof configjson.content === 'object' && !Array.isArray(configjson.content)
      ? configjson.content
      : {};
    configjson.content.ui = configjson.content.ui && typeof configjson.content.ui === 'object' && !Array.isArray(configjson.content.ui)
      ? configjson.content.ui
      : {};

    for (const configKey of SHOPPING_CART_CONFIG_KEYS) {
      const control = this.form.get(configKey);
      if (!control?.dirty) {
        continue;
      }
      const nextValue = Boolean(control.value);
      if (currentState[configKey].exists && currentState[configKey].value === nextValue) {
        continue;
      }
      configjson.content.ui[configKey] = nextValue;
      hasChanges = true;
    }

    if (!hasChanges) {
      return null;
    }

    return {
      partnerId: String(partner.partnerId),
      configjson,
    };
  }

  private getSelectedVendorId(): string {
    return String(this.form.get('vendorId')?.value || '').trim();
  }

  private getSelectedPartner(): Partner | null {
    const vendorId = this.getSelectedVendorId();
    if (!vendorId || vendorId === SELECT_VENDOR_VALUE || vendorId === 'ALL') {
      return null;
    }
    return (this.data.partners || []).find((partner) => String(partner.partnerId || '') === vendorId) || null;
  }

  private getPartnerShoppingCartConfigState(partner: Partner): Record<typeof SHOPPING_CART_CONFIG_KEYS[number], { exists: boolean; value: boolean }> {
    const configjson = this.clonePartnerConfigJson(partner?.configjson);
    const uiConfig = configjson?.content?.ui && typeof configjson.content.ui === 'object'
      ? configjson.content.ui
      : {};

    return {
      shoppingCart: {
        exists: Object.prototype.hasOwnProperty.call(uiConfig, 'shoppingCart'),
        value: Boolean(uiConfig.shoppingCart),
      },
      hideEnrollButtonForShoppingCart: {
        exists: Object.prototype.hasOwnProperty.call(uiConfig, 'hideEnrollButtonForShoppingCart'),
        value: Boolean(uiConfig.hideEnrollButtonForShoppingCart),
      },
    };
  }

  private clonePartnerConfigJson(rawConfigJson: any): Record<string, any> {
    if (!rawConfigJson) {
      return {};
    }
    if (typeof rawConfigJson === 'string') {
      try {
        const parsedConfig = JSON.parse(rawConfigJson);
        return parsedConfig && typeof parsedConfig === 'object' && !Array.isArray(parsedConfig) ? parsedConfig : {};
      } catch (_) {
        return {};
      }
    }
    try {
      const clonedConfig = JSON.parse(JSON.stringify(rawConfigJson));
      return clonedConfig && typeof clonedConfig === 'object' && !Array.isArray(clonedConfig) ? clonedConfig : {};
    } catch (_) {
      return {};
    }
  }

  protected onAppliesToAllProductsChanged(isChecked: boolean): void {
    if (isChecked) {
      this.form.patchValue({ chargeMode: 'line_item' }, { emitEvent: false });
    }
    this.refreshDerivedState();
  }

  private computeDuplicateRuleError(): string | null {
    if (!Boolean(this.form.get('useConditionalRules')?.value)) {
      return null;
    }

    const signatures = new Set<string>();
    const duplicates = new Set<string>();

    this.compileRulesForStorage().forEach((rule) => {
      const signature = [
        [...rule.includePlans].map((value) => this.normalizePlanValue(value)).sort().join(','),
        [...rule.excludePlans].map((value) => this.normalizePlanValue(value)).sort().join(','),
        [...rule.includeStates].map((value) => value.toLowerCase()).sort().join(','),
        [...rule.excludeStates].map((value) => value.toLowerCase()).sort().join(','),
      ].join('||');
      if (signatures.has(signature)) {
        duplicates.add(signature);
      } else {
        signatures.add(signature);
      }
    });

    if (duplicates.size > 0) {
      return 'Duplicate rule conditions detected. Keep only one rule per unique plan/state condition.';
    }
    return null;
  }

  private refreshPreview(): void {
    if (!Boolean(this.form.get('useConditionalRules')?.value)) {
      const baseAmountCents = this.toWholeNumber(this.form.get('amountCents')?.value);
      this.previewAmountCents = baseAmountCents === null ? null : baseAmountCents;
      this.previewMatchedRuleSummary = 'Base amount';
      this.previewWarning = null;
      return;
    }

    const defaultAmountCents = this.toWholeNumber(this.form.get('defaultAmountCents')?.value);
    const fallbackDefault = defaultAmountCents === null ? 0 : defaultAmountCents;
    const previewPlanLevel = this.normalizePlanValue(this.form.get('previewPlanLevel')?.value);
    const previewState = this.normalizeStateValue(this.form.get('previewState')?.value);
    const rules = this.compileRulesForStorage();

    let matchedRuleIndex = -1;
    let matchedSpecificity = -1;
    let matchedAmount: number | null = null;
    rules.forEach((rule, index) => {
      if (rule.amountCents === null) {
        return;
      }
      const normalizedIncludePlans = rule.includePlans.map((value) => this.normalizePlanValue(value));
      const normalizedExcludePlans = rule.excludePlans.map((value) => this.normalizePlanValue(value));
      const planMatches = this.matchDimension(previewPlanLevel, normalizedIncludePlans, normalizedExcludePlans);
      const stateMatches = this.matchDimension(previewState, rule.includeStates, rule.excludeStates);
      if (!planMatches || !stateMatches) {
        return;
      }

      const specificity =
        ((rule.includePlans.length > 0 || rule.excludePlans.length > 0) ? 1 : 0)
        + ((rule.includeStates.length > 0 || rule.excludeStates.length > 0) ? 1 : 0);

      if (specificity >= matchedSpecificity) {
        matchedSpecificity = specificity;
        matchedRuleIndex = index;
        matchedAmount = rule.amountCents;
      }
    });

    if (matchedAmount !== null) {
      this.previewAmountCents = matchedAmount;
      this.previewMatchedRuleSummary = `Rule #${matchedRuleIndex + 1}`;
    } else {
      this.previewAmountCents = fallbackDefault;
      this.previewMatchedRuleSummary = 'Default amount';
    }

    if (!previewState) {
      this.previewWarning = 'Select a preview state to test state-based conditions.';
      return;
    }
    this.previewWarning = null;
  }

  private normalizeRulesFromAppliesWhen(appliesWhen: Record<string, any>, fallbackAmountCents: number): {
    defaultAmountCents: number;
    rules: NormalizedRule[];
  } {
    const rulesFromAppliesWhen = appliesWhen?.amountRules;
    let rulesSource: Array<Record<string, any>> = [];
    let defaultAmountCents = fallbackAmountCents;

    if (Array.isArray(rulesFromAppliesWhen)) {
      rulesSource = rulesFromAppliesWhen;
      const rootDefault = this.toWholeNumber(appliesWhen?.defaultAmountCents);
      if (rootDefault !== null) {
        defaultAmountCents = rootDefault;
      }
    } else if (rulesFromAppliesWhen && typeof rulesFromAppliesWhen === 'object') {
      rulesSource = Array.isArray(rulesFromAppliesWhen.rules) ? rulesFromAppliesWhen.rules : [];
      const objectDefault = this.toWholeNumber(rulesFromAppliesWhen.defaultAmountCents);
      const rootDefault = this.toWholeNumber(appliesWhen?.defaultAmountCents);
      if (objectDefault !== null) {
        defaultAmountCents = objectDefault;
      } else if (rootDefault !== null) {
        defaultAmountCents = rootDefault;
      }
    } else {
      const rootDefault = this.toWholeNumber(appliesWhen?.defaultAmountCents);
      if (rootDefault !== null) {
        defaultAmountCents = rootDefault;
      }
    }

    const normalizedRules = rulesSource.map((rawRule) => {
      const includePlans = this.normalizePlanLevels(
        rawRule?.planLevels ?? rawRule?.planLevel ?? rawRule?.plans ?? rawRule?.plan
      );
      const excludePlans = this.normalizePlanLevels(
        rawRule?.excludePlanLevels ?? rawRule?.excludedPlanLevels ?? rawRule?.excludePlans
      );
      const includeStates = this.normalizeStates(rawRule?.states ?? rawRule?.state ?? rawRule?.stateCodes);
      const excludeStates = this.normalizeStates(rawRule?.excludeStates ?? rawRule?.excludedStates);
      return {
        amountCents: this.toWholeNumber(rawRule?.amountCents),
        planCondition: includePlans.length > 0 ? 'ONLY' : (excludePlans.length > 0 ? 'EXCEPT' : 'ALL'),
        planLevels: includePlans.length > 0 ? includePlans : excludePlans,
        stateCondition: includeStates.length > 0 ? 'ONLY' : (excludeStates.length > 0 ? 'EXCEPT' : 'ALL'),
        states: includeStates.length > 0 ? includeStates : excludeStates,
      } as NormalizedRule;
    });

    return {
      defaultAmountCents,
      rules: normalizedRules,
    };
  }

  private compileRulesForStorage(): Array<{
    amountCents: number | null;
    includePlans: string[];
    excludePlans: string[];
    includeStates: string[];
    excludeStates: string[];
  }> {
    return this.rules.controls.map((ruleControl) => {
      const group = ruleControl as UntypedFormGroup;
      const planCondition = this.getRuleCondition(group, 'planCondition');
      const stateCondition = this.getRuleCondition(group, 'stateCondition');
      const planLevels = this.parseCsv(group.get('planLevelsInput')?.value);
      const states = this.normalizeStates(group.get('states')?.value);
      return {
        amountCents: this.toWholeNumber(group.get('amountCents')?.value),
        includePlans: planCondition === 'ONLY' ? planLevels : [],
        excludePlans: planCondition === 'EXCEPT' ? planLevels : [],
        includeStates: stateCondition === 'ONLY' ? states : [],
        excludeStates: stateCondition === 'EXCEPT' ? states : [],
      };
    });
  }

  private getRuleCondition(ruleGroup: UntypedFormGroup, controlName: 'planCondition' | 'stateCondition'): RuleCondition {
    const value = String(ruleGroup.get(controlName)?.value || 'ALL').toUpperCase();
    if (value === 'ONLY' || value === 'EXCEPT') {
      return value;
    }
    return 'ALL';
  }

  private parseCsv(rawValue: any): string[] {
    if (typeof rawValue !== 'string') {
      return [];
    }
    return rawValue
      .split(',')
      .map((value) => value.trim())
      .filter((value) => Boolean(value));
  }

  private normalizePlanLevels(rawValue: any): string[] {
    if (Array.isArray(rawValue)) {
      return rawValue
        .map((value) => String(value || '').trim())
        .filter((value) => Boolean(value));
    }
    return this.parseCsv(rawValue);
  }

  private normalizeStates(rawValue: any): string[] {
    if (Array.isArray(rawValue)) {
      return rawValue
        .map((value) => this.normalizeStateValue(value))
        .filter((value) => Boolean(value));
    }
    const csvStates = this.parseCsv(rawValue);
    return csvStates
      .map((value) => this.normalizeStateValue(value))
      .filter((value) => Boolean(value));
  }

  private normalizeStateValue(rawValue: any): string {
    if (typeof rawValue !== 'string') {
      return '';
    }
    return rawValue.trim().toUpperCase();
  }

  private normalizePlanValue(rawValue: any): string {
    if (typeof rawValue !== 'string') {
      return '';
    }
    return rawValue
      .trim()
      .toLowerCase()
      .replace(/\bplan\b/g, '')
      .replace(/[^a-z0-9]/g, '');
  }

  private normalizeOptionalHtml(rawValue: any): string {
    if (typeof rawValue !== 'string') {
      return '';
    }
    return rawValue.trim();
  }

  private toWholeNumber(rawValue: any): number | null {
    if (rawValue === undefined || rawValue === null || rawValue === '') {
      return null;
    }
    const parsed = Number(rawValue);
    if (!Number.isFinite(parsed)) {
      return null;
    }
    return Math.round(parsed);
  }

  private matchDimension(normalizedValue: string, includeValues: string[], excludeValues: string[]): boolean {
    const normalizedCurrent = normalizedValue.toLowerCase();
    const normalizedIncludes = includeValues.map((value) => value.toLowerCase());
    const normalizedExcludes = excludeValues.map((value) => value.toLowerCase());

    if (includeValues.length > 0) {
      if (!normalizedCurrent) {
        return false;
      }
      if (!normalizedIncludes.includes(normalizedCurrent)) {
        return false;
      }
    }
    if (excludeValues.length > 0) {
      if (!normalizedCurrent) {
        return false;
      }
      if (normalizedExcludes.includes(normalizedCurrent)) {
        return false;
      }
    }
    return true;
  }

  protected requiresStripeProduct(): boolean {
    const appliesToAllProducts = Boolean(this.form.get('appliesToAllProducts')?.value);
    return appliesToAllProducts || this.getChargeMode() === 'line_item';
  }

  private getChargeMode(): FeeChargeMode {
    const appliesToAllProducts = Boolean(this.form.get('appliesToAllProducts')?.value);
    const hasStripeProduct = Boolean(String(this.form.get('stripeProductId')?.value || '').trim());
    return this.normalizeChargeMode(this.form.get('chargeMode')?.value, appliesToAllProducts, hasStripeProduct);
  }

  private normalizeChargeMode(rawValue: any, appliesToAllProducts = false, hasStripeProduct = false): FeeChargeMode {
    const mode = String(rawValue || '').trim().toLowerCase();
    if (mode === 'line_item') {
      return 'line_item';
    }
    if (mode === 'bundled' && !appliesToAllProducts) {
      return 'bundled';
    }
    if (hasStripeProduct) {
      return 'line_item';
    }
    return appliesToAllProducts ? 'line_item' : 'bundled';
  }

  private enforceScopeAndChargeModeConstraints(): void {
    const appliesToAllProducts = Boolean(this.form.get('appliesToAllProducts')?.value);
    const hasStripeProduct = Boolean(String(this.form.get('stripeProductId')?.value || '').trim());
    const normalizedMode = this.normalizeChargeMode(
      this.form.get('chargeMode')?.value,
      appliesToAllProducts,
      hasStripeProduct
    );
    if (this.form.get('chargeMode')?.value !== normalizedMode) {
      this.form.patchValue({ chargeMode: normalizedMode }, { emitEvent: false });
    }
  }
}
